import type { Address } from "@/interfaces/address";
import { MutationParams } from "@/interfaces/mutationParams";
import axios from "@/lib/axios";
import { useMutation, useQuery } from "@tanstack/vue-query";
import { useToast } from "vue-toastification";
import useCustomMutation from "./useCustomMutation";

interface Props {
  params?: Params;
  immediate?: boolean;
}

interface Params extends Omit<Partial<Address>, "id"> {
  id: string | number;
  includes?: string[];
}

interface CreateParams {
  number: string | number;
  internalNumber: string | number;
  street: string;
  neighborhood: string;
  zipcode: string;
  city: string;
  country: string;
  state: string;
  latitude?: number | string;
  longitude?: number | string;
  addressable_type?: string;
  addressable_id?: number | string;
}

interface UpdateParams extends CreateParams {
  id: string | number;
}

const getAddress = async (params?: Params): Promise<Address | void> => {
  if (!params) return;
  const { data } = await axios.get<Address>(`/api/addresses/${params.id}`, {
    params,
  });
  return data;
};

export const createAddress = async (params: CreateParams) => {
  const { data } = await axios.post<Address>("/api/addresses", params);
  return data;
};

export const updateAddress = async (params: UpdateParams) => {
  const { data } = await axios.put<Address>(
    `/api/addresses/${params.id}`,
    params,
  );
  return data;
};

const useAddress = (props?: Props) => {
  const { params, immediate = true } = props || {};
  const toast = useToast();

  const { data, isLoading } = useQuery(
    ["address", params?.id],
    () => getAddress(params),
    {
      enabled: immediate,
      retry: false,
    },
  );

  const createMutation = useMutation(
    (props: MutationParams<CreateParams>) => createAddress(props.params),
    useCustomMutation(toast, "create"),
  );

  const updateMutation = useMutation(
    (props: MutationParams<UpdateParams>) => updateAddress(props.params),
    useCustomMutation(toast, "update"),
  );

  return {
    addresses: data,
    isLoading,

    createAddressMutation: createMutation,
    createAddress: createMutation.mutate,
    createAddressAsync: createMutation.mutateAsync,

    updateAddressMutation: updateMutation,
    updateAddress: updateMutation.mutate,
    updateAddressAsync: updateMutation.mutateAsync,
    updateAddressLoading: updateMutation.isLoading,
  };
};

export default useAddress;
